import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Autocomplete,
  Badge,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputBase,
  LinearProgress,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import FilterListIcon from "@mui/icons-material/FilterList";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import RefreshIcon from "@mui/icons-material/Refresh";
import AddIcon from "@mui/icons-material/Add";
import ListAltIcon from "@mui/icons-material/ListAlt";
import CommentIcon from "@mui/icons-material/AddComment";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ArchiveOutlinedIcon from "@mui/icons-material/ArchiveOutlined";

import API_BASE_URL from "../../config/api";
import { COLORS } from "../UserDashboard/dashboardConfig";
import { usePageStyles } from "../common/pageStyles";
import { useSession } from "../common/useSession";
import { useViewPreferences } from "../common/useViewPreferences";
import FilterDrawer, { FilterCard, countFilled } from "../common/FilterDrawer";
import ColumnsMenu from "../common/ColumnsMenu";

const BASE_URL = API_BASE_URL;
const VIEW = "kontrolaWewnetrzna";
const KW_FILTERS_STORAGE_KEY = "kw_list_filters";
const COLUMNS_STORAGE_KEY = "columnsView.kontrolaWewnetrzna";
// Backend ogranicza limit do 100 wierszy na stronę.
const PAGE_SIZE_OPTIONS = [20, 50, 100];
const FILTER_DEBOUNCE_MS = 300;

const EMPTY_FILTERS = {
  search: "",
  mrn: "",
  status: "",
  duplikat: "",
  kontrolujacy: "",
  podpisany: "",
  nrRef: "",
  kwalifikujeDoKorekty: "",
  czyJestWRw: "",
  powodWpisania: "",
};

// Wartości w selectach filtrów (backend rozpoznaje dokładnie te teksty).
const STATUS_OPTIONS = ["KW rozpoczęta", "KW zakończona"];
const KORYGOWANIE_OPTIONS = ["PUSTE", "TAK", "NIE", "DO OMÓWIENIA"];
const YES_NO_OPTIONS = ["TAK", "NIE"];

// Kolumny w menu "Kolumny". MRN (pierwsza, lepka kolumna) jest zawsze widoczny.
const COLUMNS = [
  { id: "powodWpisania", label: "Powód wpisania", width: 190 },
  { id: "dataSAD", label: "Data SAD", width: 120 },
  { id: "nrAkt", label: "Nr akt", width: 160 },
  { id: "nadawca", label: "Nadawca", width: 190 },
  { id: "odbiorca", label: "Odbiorca", width: 190 },
  { id: "kontrolujacy", label: "Kontrolujący", width: 160 },
  { id: "podpisany", label: "Podpisany", width: 160 },
  { id: "kwalifikujeDoKorekty", label: "Kwalifikuje do korekty", width: 180 },
  { id: "wniosekWRw", label: "Czy jest już w RW", width: 150 },
  { id: "status", label: "Status", width: 160 },
  { id: "duplikat", label: "Duplikat", width: 110 },
  { id: "createdAt", label: "Data utworzenia", width: 170 },
];

const DEFAULT_HIDDEN_COLUMNS = [];

// Minimalna szerokość lepkiej kolumny MRN; dłuższy numer poszerza kolumnę (nie jest ucinany).
const STICKY_MRN_WIDTH = 190;

const PILL_TONES = {
  green: { bg: "#e8f5e9", fg: "#1b5e20" },
  amber: { bg: "#fff8e1", fg: "#b26a00" },
  orange: { bg: "#fff3e0", fg: "#e65100" },
  red: { bg: "#ffebee", fg: "#c62828" },
  blue: { bg: "#e3f2fd", fg: "#1565c0" },
  gray: { bg: "#eeeeee", fg: "#616161" },
};

const readSavedFilters = () => {
  try {
    const raw = localStorage.getItem(KW_FILTERS_STORAGE_KEY);
    if (!raw) return EMPTY_FILTERS;
    const saved = JSON.parse(raw);
    return { ...EMPTY_FILTERS, ...(saved?.applied || {}) };
  } catch (e) {
    return EMPTY_FILTERS;
  }
};

const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
  "Content-Type": "application/json",
});

const formatHistoryDate = (value) => {
  const text = String(value || "").trim();
  if (!text) return "-";

  const match = text.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/);
  if (match) {
    const [, year, month, dayOfMonth, hour, minute] = match;
    return `${dayOfMonth}.${month}.${year}, ${hour}:${minute}`;
  }

  const date = new Date(text);
  if (Number.isNaN(date.getTime())) return text;

  const pad = (number) => String(number).padStart(2, "0");
  return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()}, ${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const formatWniosekWRw = (kwalifikujeDoKorekty, wniosekWRw) => {
  if (kwalifikujeDoKorekty !== "TAK") return "-";
  const normalizedValue = Number(wniosekWRw);
  if (normalizedValue === 1) return "TAK";
  if (normalizedValue === 0) return "NIE";
  if (normalizedValue === 2) return "ARCHIWUM";
  return "-";
};

const useDebounced = (value, delay) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
};

const describeFilters = (f) => {
  const chips = [];
  const text = (key, label) => {
    if (f[key]) chips.push({ id: key, label: `${label}: ${f[key]}`, clear: { [key]: "" } });
  };
  text("search", "Szukaj");
  text("mrn", "MRN");
  text("nrRef", "Nr ref");
  text("status", "Status");
  if (f.duplikat) {
    chips.push({
      id: "duplikat",
      label: `Duplikat: ${f.duplikat === "1" ? "TAK" : "NIE"}`,
      clear: { duplikat: "" },
    });
  }
  text("kontrolujacy", "Kontrolujący");
  text("podpisany", "Podpisany");
  text("kwalifikujeDoKorekty", "Do korekty");
  text("czyJestWRw", "W RW");
  text("powodWpisania", "Powód");
  return chips;
};

const Pill = ({ tone, children }) => {
  const classes = usePageStyles();
  const colors = PILL_TONES[tone] || PILL_TONES.gray;
  return (
    <span className={classes.pill} style={{ background: colors.bg, color: colors.fg }}>
      {children}
    </span>
  );
};

const getRowTint = (item, classes) => {
  const pendingRw =
    item.kwalifikujeDoKorekty === "TAK" && Number(item.wniosekWRw) === 0;
  const isDuplicate = Boolean(item.duplikat);
  if (pendingRw && isDuplicate) return classes.rowAlert;
  if (pendingRw) return classes.rowWarn;
  if (isDuplicate) return classes.rowDanger;
  if (formatWniosekWRw(item.kwalifikujeDoKorekty, item.wniosekWRw) === "ARCHIWUM") {
    return classes.rowMuted;
  }
  return "";
};

const KwRow = React.memo(
  ({ item, columns, selected, onSelect, expanded, details, onToggle, onOpen, onArchive, isAdmin }) => {
    const classes = usePageStyles();
    const empty = <span className={classes.muted}>—</span>;
    const text = (value) =>
      value ? (
        <Tooltip title={String(value)} enterDelay={600}>
          <span className={classes.ellipsis}>{value}</span>
        </Tooltip>
      ) : (
        empty
      );

    const renderCell = (id) => {
      switch (id) {
        case "kwalifikujeDoKorekty": {
          const value = item.kwalifikujeDoKorekty;
          if (!value) return empty;
          const tone =
            value === "TAK" ? "green" : value === "DO OMÓWIENIA" ? "orange" : "gray";
          return <Pill tone={tone}>{value}</Pill>;
        }
        case "wniosekWRw": {
          const value = formatWniosekWRw(item.kwalifikujeDoKorekty, item.wniosekWRw);
          if (value === "-") return empty;
          const tone = value === "TAK" ? "green" : value === "NIE" ? "amber" : "gray";
          return <Pill tone={tone}>{value}</Pill>;
        }
        case "status": {
          if (!item.status) return empty;
          return <Pill tone={item.status === "KW zakończona" ? "green" : "blue"}>{item.status}</Pill>;
        }
        case "duplikat":
          return item.duplikat ? <Pill tone="red">Duplikat</Pill> : empty;
        case "createdAt":
          return formatHistoryDate(item.createdAt);
        default:
          return text(item[id]);
      }
    };

    return (
      <>
        <TableRow
          hover={false}
          className={`${classes.row} ${getRowTint(item, classes)} ${selected ? classes.rowSelected : ""}`}
        >
          <TableCell
            padding="checkbox"
            className={classes.stickyCell}
            style={{ left: 0, width: 52, minWidth: 52 }}
          >
            <Checkbox
              size="small"
              color="primary"
              checked={selected}
              onChange={() => onSelect(item.id)}
              inputProps={{ "aria-label": `Wybierz wpis ${item.mrn}` }}
            />
          </TableCell>
          <TableCell
            padding="none"
            className={classes.stickyCell}
            style={{ left: 52, width: 40, minWidth: 40 }}
          >
            <Tooltip title="Historia zmian i komentarze">
              <IconButton size="small" onClick={() => onToggle(item.id)} aria-label="Pokaż historię">
                {details?.loading ? (
                  <CircularProgress size={18} />
                ) : expanded ? (
                  <KeyboardArrowUpIcon fontSize="small" />
                ) : (
                  <KeyboardArrowDownIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
          </TableCell>
          <TableCell
            className={`${classes.stickyCell} ${classes.stickyEdge}`}
            style={{ left: 92, minWidth: STICKY_MRN_WIDTH, whiteSpace: "nowrap" }}
          >
            <button type="button" className={classes.link} onClick={() => onOpen(item.id)}>
              {item.mrn || "—"}
            </button>
          </TableCell>
          {columns.map((column) => (
            <TableCell
              key={column.id}
              style={{ minWidth: column.width, maxWidth: column.width }}
            >
              {renderCell(column.id)}
            </TableCell>
          ))}
          <TableCell style={{ width: 96, minWidth: 96 }}>
            <div className={classes.actions}>
              <Tooltip title="Otwórz szczegóły">
                <IconButton size="small" onClick={() => onOpen(item.id)} aria-label="Otwórz szczegóły">
                  <OpenInNewIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              {isAdmin && (
                <Tooltip title="Archiwizuj wpis">
                  <IconButton size="small" onClick={() => onArchive(item)} aria-label="Archiwizuj wpis">
                    <ArchiveOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </div>
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell
            colSpan={columns.length + 4}
            className={`${classes.detailCell} ${expanded ? classes.detailCellOpen : ""}`}
          >
            <Collapse in={expanded} timeout="auto" unmountOnExit>
              <div className={classes.detailWrap}>
                <div className={classes.detailCard}>
                  <div className={classes.detailTitle}>Historia zmian</div>
                  <div className={classes.detailList}>
                    {!details?.history?.length ? (
                      <div className={classes.detailEmpty}>Brak historii zmian dla tego wpisu.</div>
                    ) : (
                      details.history.map((change) => (
                        <div key={change.id} className={classes.detailItem}>
                          <strong>{change.fieldName || "-"}</strong>
                          <div className={classes.detailChange}>
                            <span style={{ color: COLORS.danger }}>{change.oldValue || "—"}</span>
                            {" → "}
                            <span style={{ color: COLORS.success, fontWeight: 600 }}>
                              {change.newValue || "—"}
                            </span>
                          </div>
                          <div className={classes.detailMeta}>
                            {change.editedBy || "-"} · {formatHistoryDate(change.editedAt)}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
                <div className={classes.detailCard}>
                  <div className={classes.detailTitle}>Komentarze</div>
                  <div className={classes.detailList}>
                    {!details?.comments?.length ? (
                      <div className={classes.detailEmpty}>Brak komentarzy dla tego wpisu.</div>
                    ) : (
                      details.comments.map((comment) => (
                        <div key={comment.id} className={classes.detailItem}>
                          <div>{comment.comment || "-"}</div>
                          <div className={classes.detailMeta}>
                            {comment.username || "-"} · {formatHistoryDate(comment.createdAt)}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            </Collapse>
          </TableCell>
        </TableRow>
      </>
    );
  },
);

const KontrolaWewnetrzna = () => {
  const classes = usePageStyles();
  const navigate = useNavigate();
  const { username, isAdmin } = useSession();

  const prefs = useViewPreferences({
    view: VIEW,
    storageKey: COLUMNS_STORAGE_KEY,
    defaultHiddenColumns: DEFAULT_HIDDEN_COLUMNS,
    pageSizeOptions: PAGE_SIZE_OPTIONS,
    defaultPageSize: 20,
  });
  const pageSize = prefs.pageSize;

  const [entries, setEntries] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [filters, setFilters] = useState(readSavedFilters);
  const [powodyWpisaniaList, setPowodyWpisaniaList] = useState([]);
  const [uzytkownicyList, setUzytkownicyList] = useState([]);

  const [filtersOpen, setFiltersOpen] = useState(false);
  const [columnsAnchor, setColumnsAnchor] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [detailsById, setDetailsById] = useState({});
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const [bulkSaving, setBulkSaving] = useState(false);
  const [archiveTarget, setArchiveTarget] = useState(null);
  const searchRef = useRef(null);
  const requestId = useRef(0);

  const debouncedFilters = useDebounced(filters, FILTER_DEBOUNCE_MS);

  // Filtry pamiętane między sesjami (ten sam klucz i kształt co w poprzedniej wersji).
  useEffect(() => {
    try {
      localStorage.setItem(
        KW_FILTERS_STORAGE_KEY,
        JSON.stringify({ applied: filters, inputs: filters }),
      );
    } catch (e) {
      // brak miejsca - filtry po prostu nie zostaną zapamiętane
    }
  }, [filters]);

  const fetchEntries = useCallback(async () => {
    const id = ++requestId.current;
    setLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}/kw-dane`, {
        headers: getAuthHeaders(),
        params: { page: page + 1, limit: pageSize, ...debouncedFilters },
      });
      if (id !== requestId.current) return;
      setEntries(Array.isArray(response.data?.items) ? response.data.items : []);
      setTotalRows(Number(response.data?.pagination?.total || 0));
      setError(null);
    } catch (err) {
      if (id !== requestId.current) return;
      console.error("Błąd podczas pobierania wpisów KW:", err);
      setError("Nie udało się pobrać zapisanych wpisów KW. Spróbuj odświeżyć widok.");
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  }, [page, pageSize, debouncedFilters]);

  // Pierwsze zapytanie dopiero po wczytaniu preferencji (rozmiar strony), bez podwójnego pobierania.
  useEffect(() => {
    if (prefs.ready) fetchEntries();
  }, [prefs.ready, fetchEntries]);

  // Zmiana strony / filtrów czyści zaznaczenie (zaznaczone są wiersze bieżącej strony).
  useEffect(() => {
    setSelectedIds([]);
  }, [page, pageSize, debouncedFilters]);

  useEffect(() => {
    axios
      .get(`${BASE_URL}/list/uzytkownicy`, { headers: getAuthHeaders() })
      .then((response) =>
        setUzytkownicyList(
          Array.isArray(response.data)
            ? [...response.data].sort((a, b) =>
                (a?.name || "").localeCompare(b?.name || "", "pl", { sensitivity: "base" }),
              )
            : [],
        ),
      )
      .catch((err) => {
        console.error("Błąd podczas pobierania listy użytkowników:", err);
        toast.error("Nie udało się pobrać listy użytkowników.");
      });
    axios
      .get(`${BASE_URL}/list/kwPowodyWpisania`, { headers: getAuthHeaders() })
      .then((response) =>
        setPowodyWpisaniaList(Array.isArray(response.data) ? response.data : []),
      )
      .catch((err) => console.error("Błąd podczas pobierania powodów wpisania:", err));
  }, []);

  // Skrót "/" ustawia fokus w wyszukiwarce.
  useEffect(() => {
    const onKey = (event) => {
      const tag = (event.target.tagName || "").toLowerCase();
      if (event.key === "/" && !["input", "textarea", "select"].includes(tag)) {
        event.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const updateFilters = useCallback((patch) => {
    setFilters((prev) => ({ ...prev, ...patch }));
    setPage(0);
  }, []);

  const clearAllFilters = () => {
    setFilters(EMPTY_FILTERS);
    setPage(0);
  };

  const activeChips = useMemo(() => describeFilters(filters), [filters]);
  const activeCount = useMemo(
    () => describeFilters({ ...filters, search: "" }).length,
    [filters],
  );
  const hasFilters = activeChips.length > 0;

  const columns = useMemo(
    () => COLUMNS.filter((c) => !prefs.hiddenColumns.includes(c.id)),
    [prefs.hiddenColumns],
  );

  const presets = [
    {
      id: "doKorekty",
      label: "Do korekty, bez wpisu w RW",
      hint: "Kwalifikuje się do korekty i nie ma jeszcze wpisu w RW",
      active: filters.kwalifikujeDoKorekty === "TAK" && filters.czyJestWRw === "NIE",
      patch: { kwalifikujeDoKorekty: "TAK", czyJestWRw: "NIE" },
      reset: { kwalifikujeDoKorekty: "", czyJestWRw: "" },
    },
    {
      id: "duplikaty",
      label: "Duplikaty",
      hint: "Wpisy z duplikatem MRN",
      active: filters.duplikat === "1",
      patch: { duplikat: "1" },
      reset: { duplikat: "" },
    },
    {
      id: "rozpoczete",
      label: "KW rozpoczęte",
      hint: "Kontrole, które jeszcze trwają",
      active: filters.status === "KW rozpoczęta",
      patch: { status: "KW rozpoczęta" },
      reset: { status: "" },
    },
  ];

  // ---- Rozwijanie historii i komentarzy ----
  const handleToggleExpand = useCallback(
    async (id) => {
      const willOpen = !expanded[id];
      setExpanded((prev) => ({ ...prev, [id]: willOpen }));
      if (!willOpen || detailsById[id]?.loaded) return;

      setDetailsById((prev) => ({ ...prev, [id]: { ...prev[id], loading: true } }));
      const headers = getAuthHeaders();
      const [history, comments] = await Promise.all([
        axios
          .get(`${BASE_URL}/kw-dane/${id}/history`, { headers })
          .then((r) => (Array.isArray(r.data) ? r.data : []))
          .catch((err) => {
            console.error("Błąd podczas pobierania historii zmian KW:", err);
            toast.error("Nie udało się pobrać historii zmian.");
            return [];
          }),
        axios
          .get(`${BASE_URL}/kw-comments/${id}`, { headers })
          .then((r) => (Array.isArray(r.data) ? r.data : []))
          .catch((err) => {
            console.error("Błąd podczas pobierania komentarzy KW:", err);
            toast.error("Nie udało się pobrać komentarzy.");
            return [];
          }),
      ]);
      setDetailsById((prev) => ({
        ...prev,
        [id]: { history, comments, loading: false, loaded: true },
      }));
    },
    [expanded, detailsById],
  );

  const handleOpenDetails = useCallback(
    (id) => navigate(`/kontrolaWewnetrzna/szczegoly/${id}`),
    [navigate],
  );

  // ---- Zaznaczanie ----
  const handleSelect = useCallback((id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((rowId) => rowId !== id) : [...prev, id],
    );
  }, []);

  const selectedRows = useMemo(
    () => entries.filter((item) => selectedIds.includes(item.id)),
    [entries, selectedIds],
  );
  const allSelected = entries.length > 0 && entries.every((item) => selectedIds.includes(item.id));
  const someSelected = selectedIds.length > 0 && !allSelected;

  const handleSelectPage = (event) =>
    setSelectedIds(event.target.checked ? entries.map((item) => item.id) : []);

  const buildRecipientsForEntry = (item) =>
    [...new Set([item?.podpisany, item?.kontrolujacy])]
      .map((recipient) => String(recipient || "").trim())
      .filter(Boolean)
      .filter((recipient) => recipient !== username);

  const handleSaveBulkComment = async () => {
    const trimmedComment = bulkText.trim();
    if (!trimmedComment) {
      toast.warning("Wpisz treść komentarza.");
      return;
    }
    if (selectedRows.length === 0) {
      toast.warning("Brak zaznaczonych wpisów KW do zapisu komentarza.");
      return;
    }
    try {
      setBulkSaving(true);
      const items = selectedRows.map((row) => ({
        id: row.id,
        mrn: row.mrn,
        status: row.status,
        podpisany: row.podpisany,
        recipients: buildRecipientsForEntry(row),
      }));
      const response = await axios.post(
        `${BASE_URL}/kw-comments/bulk/addWithNotification`,
        { items, comment: trimmedComment },
        { headers: getAuthHeaders() },
      );
      toast.success(`Komentarz zapisano dla ${response?.data?.processed ?? items.length} wpisów KW.`);
      setBulkText("");
      setSelectedIds([]);
      setBulkOpen(false);
      // Zapisane komentarze mogły już być wczytane - wymuś ponowne pobranie przy kolejnym otwarciu.
      setDetailsById({});
      await fetchEntries();
    } catch (err) {
      toast.error(
        err?.response?.data?.message || "Nie udało się zapisać komentarzy zbiorczych dla KW.",
      );
    } finally {
      setBulkSaving(false);
    }
  };

  const handleConfirmArchive = async () => {
    const row = archiveTarget;
    if (!row || !isAdmin) return;
    try {
      await axios.patch(`${BASE_URL}/kw-dane/${row.id}/archive`, {}, { headers: getAuthHeaders() });
      toast.success(`Wpis ${row.mrn} został zarchiwizowany.`, { autoClose: 3000 });
      setArchiveTarget(null);
      if (entries.length === 1 && page > 0) setPage((prev) => Math.max(prev - 1, 0));
      else fetchEntries();
    } catch (err) {
      console.error("Błąd podczas archiwizacji wpisu KW:", err);
      toast.error(err.response?.data?.message || "Nie udało się zarchiwizować wpisu KW.");
    }
  };

  // ---- Pola panelu filtrów ----
  const text = (key, label, extra = {}) => (
    <TextField
      label={label}
      size="small"
      fullWidth
      autoComplete="off"
      value={filters[key]}
      onChange={(e) => updateFilters({ [key]: e.target.value })}
      {...extra}
    />
  );

  const select = (key, label, items, renderLabel = (v) => v) => (
    <TextField
      select
      label={label}
      size="small"
      fullWidth
      value={filters[key]}
      onChange={(e) => updateFilters({ [key]: e.target.value })}
    >
      <MenuItem value="" style={{ fontWeight: "bold" }}>
        Wszystkie
      </MenuItem>
      {items.map((item) => (
        <MenuItem key={item} value={item}>
          {renderLabel(item)}
        </MenuItem>
      ))}
    </TextField>
  );

  const person = (key, label) => (
    <Autocomplete
      size="small"
      options={uzytkownicyList.map((u) => u.name)}
      value={filters[key] || null}
      onChange={(_, value) => updateFilters({ [key]: value || "" })}
      noOptionsText="Brak opcji"
      renderInput={(params) => <TextField {...params} label={label} />}
    />
  );

  const headerSticky = [
    { left: 0, width: 52 },
    { left: 52, width: 40 },
    { left: 92, width: STICKY_MRN_WIDTH },
  ];

  return (
    <>
      <div className={classes.pageHeader}>
        <div>
          <h1 className={classes.title}>Kontrola wewnętrzna</h1>
          <div className={classes.subtitle}>
            <span>
              <strong style={{ color: "#212121" }}>{totalRows}</strong>{" "}
              {hasFilters ? "wpisów spełnia filtry" : "aktywnych wpisów KW"}
            </span>
            <Tooltip title="Odśwież teraz">
              <IconButton size="small" onClick={fetchEntries} aria-label="Odśwież">
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </div>
        </div>
        <div className={classes.headerActions}>
          <Button
            variant="outlined"
            startIcon={<ViewColumnIcon />}
            onClick={(e) => setColumnsAnchor(e.currentTarget)}
          >
            Kolumny
          </Button>
          {isAdmin && (
            <Button
              variant="outlined"
              startIcon={<ListAltIcon />}
              onClick={() => navigate("/kontrolaWewnetrzna/listy")}
            >
              Zarządzaj listami
            </Button>
          )}
          <Button
            variant="contained"
            disableElevation
            startIcon={<AddIcon />}
            onClick={() => navigate("/dodajDoKW")}
          >
            Dodaj do KW
          </Button>
        </div>
      </div>

      <div className={classes.card}>
        <div className={classes.toolbar}>
          <div className={classes.search}>
            <SearchIcon fontSize="small" />
            <InputBase
              inputRef={searchRef}
              className={classes.searchInput}
              placeholder="Szukaj po MRN, nr akt, nadawcy…"
              value={filters.search}
              onChange={(e) => updateFilters({ search: e.target.value })}
              inputProps={{ "aria-label": "Szukaj" }}
            />
            {filters.search ? (
              <IconButton size="small" onClick={() => updateFilters({ search: "" })} aria-label="Wyczyść wyszukiwanie">
                <ClearIcon fontSize="small" />
              </IconButton>
            ) : (
              <kbd className={classes.kbd}>/</kbd>
            )}
          </div>

          <Badge badgeContent={activeCount} color="primary">
            <Button
              variant={filtersOpen || activeCount > 0 ? "contained" : "outlined"}
              startIcon={<FilterListIcon />}
              onClick={() => setFiltersOpen(true)}
              disableElevation
            >
              Filtry
            </Button>
          </Badge>

          {presets.map((preset) => (
            <Tooltip key={preset.id} title={preset.hint}>
              <Chip
                className={classes.presetChip}
                label={preset.label}
                color={preset.active ? "primary" : "default"}
                variant={preset.active ? "filled" : "outlined"}
                onClick={() => updateFilters(preset.active ? preset.reset : preset.patch)}
                onDelete={preset.active ? () => updateFilters(preset.reset) : undefined}
              />
            </Tooltip>
          ))}

          <span className={classes.toolbarGap} />
          {hasFilters && (
            <Button size="small" onClick={clearAllFilters}>
              Wyczyść filtry
            </Button>
          )}
        </div>

        {hasFilters && (
          <div className={classes.activeFilters}>
            {activeChips.map((chip) => (
              <Chip
                key={chip.id}
                size="small"
                label={chip.label}
                onDelete={() => updateFilters(chip.clear)}
                style={{ background: COLORS.primarySoft, color: COLORS.primaryDark }}
              />
            ))}
          </div>
        )}

        {selectedIds.length > 0 && (
          <div className={classes.selectionBar}>
            <strong>Zaznaczono: {selectedIds.length}</strong>
            <Button size="small" variant="contained" startIcon={<CommentIcon />} onClick={() => setBulkOpen(true)} disableElevation>
              Dodaj komentarz
            </Button>
            <Button size="small" onClick={() => setSelectedIds([])}>
              Odznacz wszystko
            </Button>
          </div>
        )}

        {error && <div className={classes.error}>{error}</div>}

        <div className={classes.tableWrap}>
          {loading && <LinearProgress className={classes.progress} />}
          <Table stickyHeader size="small" style={{ opacity: loading ? 0.6 : 1, transition: "opacity 0.15s" }}>
            <TableHead>
              <TableRow>
                <TableCell
                  padding="checkbox"
                  className={`${classes.headCell} ${classes.headSticky}`}
                  style={{ left: headerSticky[0].left, width: headerSticky[0].width, minWidth: headerSticky[0].width }}
                >
                  <Checkbox
                    size="small"
                    color="primary"
                    indeterminate={someSelected}
                    checked={allSelected}
                    onChange={handleSelectPage}
                    inputProps={{ "aria-label": "Zaznacz stronę" }}
                  />
                </TableCell>
                <TableCell
                  padding="none"
                  className={`${classes.headCell} ${classes.headSticky}`}
                  style={{ left: headerSticky[1].left, width: headerSticky[1].width, minWidth: headerSticky[1].width }}
                />
                <TableCell
                  className={`${classes.headCell} ${classes.headSticky}`}
                  style={{ left: headerSticky[2].left, minWidth: headerSticky[2].width, whiteSpace: "nowrap", boxShadow: "inset -1px 0 0 #e0e0e0" }}
                >
                  MRN
                </TableCell>
                {columns.map((column) => (
                  <TableCell
                    key={column.id}
                    className={classes.headCell}
                    style={{ minWidth: column.width, maxWidth: column.width }}
                  >
                    {column.label}
                  </TableCell>
                ))}
                <TableCell className={classes.headCell} style={{ width: 96 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {entries.map((item) => (
                <KwRow
                  key={item.id}
                  item={item}
                  columns={columns}
                  selected={selectedIds.includes(item.id)}
                  onSelect={handleSelect}
                  expanded={Boolean(expanded[item.id])}
                  details={detailsById[item.id]}
                  onToggle={handleToggleExpand}
                  onOpen={handleOpenDetails}
                  onArchive={setArchiveTarget}
                  isAdmin={isAdmin}
                />
              ))}
            </TableBody>
          </Table>

          {!loading && entries.length === 0 && !error && (
            <div className={classes.empty}>
              <SearchOffIcon style={{ fontSize: 44, color: "#bdbdbd" }} />
              <strong style={{ fontSize: 17, color: "#424242" }}>
                Brak wpisów do wyświetlenia
              </strong>
              <span>
                {hasFilters
                  ? "Żaden wpis nie spełnia wybranych filtrów."
                  : "Brak aktywnych wpisów kontroli wewnętrznej."}
              </span>
              {hasFilters && (
                <Button variant="outlined" onClick={clearAllFilters}>
                  Wyczyść filtry
                </Button>
              )}
            </div>
          )}
        </div>

        <TablePagination
          component="div"
          count={totalRows}
          page={page}
          rowsPerPage={pageSize}
          rowsPerPageOptions={PAGE_SIZE_OPTIONS}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            prefs.setPageSize(Number(e.target.value));
            setPage(0);
          }}
          labelRowsPerPage="Wierszy na stronę:"
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} z ${count}`}
        />
      </div>

      <div className={classes.legend}>
        <span className={classes.legendItem}>
          <span className={classes.legendTone} style={{ background: "#fff3e0", boxShadow: "inset 3px 0 0 #e65100" }} />
          duplikat, który kwalifikuje się do korekty i nie ma jeszcze wpisu w RW
        </span>
        <span className={classes.legendItem}>
          <span className={classes.legendTone} style={{ background: "#fffde7", boxShadow: `inset 3px 0 0 ${COLORS.warning}` }} />
          kwalifikuje się do korekty i nie ma jeszcze wpisu w RW
        </span>
        <span className={classes.legendItem}>
          <span className={classes.legendTone} style={{ background: "#fff1f1", boxShadow: `inset 3px 0 0 ${COLORS.danger}` }} />
          duplikat (MRN)
        </span>
        <span className={classes.legendItem}>
          <span className={classes.legendTone} style={{ background: "#f1f2f4", boxShadow: "inset 3px 0 0 #9e9e9e" }} />
          wniosek w archiwum
        </span>
      </div>

      <FilterDrawer
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        onClear={clearAllFilters}
        activeCount={activeCount}
        total={totalRows}
        totalLabel="Wpisów"
      >
        <FilterCard title="Wyszukiwanie" count={countFilled(filters.mrn, filters.nrRef)}>
          {text("mrn", "Filtr MRN")}
          {text("nrRef", "Nr ref", { placeholder: "DS nr ref..." })}
        </FilterCard>
        <FilterCard
          title="Status i kwalifikacja"
          count={countFilled(
            filters.status,
            filters.duplikat,
            filters.kwalifikujeDoKorekty,
            filters.czyJestWRw,
            filters.powodWpisania,
          )}
        >
          {select("status", "Status", STATUS_OPTIONS)}
          {select("duplikat", "Duplikat", ["1", "0"], (v) => (v === "1" ? "TAK" : "NIE"))}
          {select("kwalifikujeDoKorekty", "Czy kwalifikuje się do korekty", KORYGOWANIE_OPTIONS)}
          {select("czyJestWRw", "Czy jest już w RW", YES_NO_OPTIONS)}
          <Autocomplete
            size="small"
            options={powodyWpisaniaList.map((p) => p.name)}
            value={filters.powodWpisania || null}
            onChange={(_, value) => updateFilters({ powodWpisania: value || "" })}
            noOptionsText="Brak opcji"
            renderInput={(params) => <TextField {...params} label="Powód wpisania" />}
          />
        </FilterCard>
        <FilterCard title="Osoby" count={countFilled(filters.kontrolujacy, filters.podpisany)}>
          {person("kontrolujacy", "Kontrolujący")}
          {person("podpisany", "Podpisany")}
        </FilterCard>
      </FilterDrawer>

      <ColumnsMenu
        anchorEl={columnsAnchor}
        onClose={() => setColumnsAnchor(null)}
        columns={COLUMNS}
        hiddenColumns={prefs.hiddenColumns}
        onToggle={prefs.toggleColumn}
        onReset={prefs.resetColumns}
      />

      <Dialog open={bulkOpen} onClose={() => !bulkSaving && setBulkOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Dodaj komentarz do {selectedIds.length} wpisów KW</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Treść komentarza"
            fullWidth
            multiline
            minRows={4}
            value={bulkText}
            onChange={(e) => setBulkText(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkOpen(false)} disabled={bulkSaving}>
            Anuluj
          </Button>
          <Button
            onClick={handleSaveBulkComment}
            variant="contained"
            disabled={bulkSaving || !bulkText.trim()}
          >
            {bulkSaving ? "Zapisywanie..." : "Zapisz komentarz"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(archiveTarget)} onClose={() => setArchiveTarget(null)}>
        <DialogTitle>Zarchiwizować wpis?</DialogTitle>
        <DialogContent>
          Wpis <strong>{archiveTarget?.mrn}</strong> zostanie przeniesiony do archiwum i zniknie z tej
          listy.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setArchiveTarget(null)}>Anuluj</Button>
          <Button onClick={handleConfirmArchive} color="error" variant="contained">
            Archiwizuj
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default KontrolaWewnetrzna;
